import { IconFont, SignTitle } from '@/components'
import { Button, Drawer, Form, Input, Select, TreeSelect, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from '@/i18n'
import {
  useAddSecurityDomain,
  useEditSecurityDomain,
  useGetClientList,
  useGetFishingEmailList,
  useGetSecurityTestEmailList,
  useGetSiteList,
} from './utils'
import { Message } from '@/components/message'
import { useGetHostList } from '@/pages/asset-management/verify-host/utils'

const { useForm } = Form

interface PropsType {
  type: string
  id: string
  visible: boolean
  name: string | undefined
  onCancel: () => void
  updateList: () => void
}

const AddDrawer: React.FC<PropsType> = ({ type, id, visible, name, onCancel, updateList }) => {
  const [form] = useForm()

  const [fishingEmailList, getFishingEmailList] = useGetFishingEmailList()
  const [siteList, getSiteList] = useGetSiteList()
  const [clientList, getClientList] = useGetClientList()
  const [securityTestEmailList, getSecurityTestEmailList] = useGetSecurityTestEmailList()
  const [addSecurityDomain, addLoading] = useAddSecurityDomain()
  const [editSecurityDomain, editLoading] = useEditSecurityDomain()
  const [getHostList] = useGetHostList()

  const [agentIds, setAgentIds] = useState<string[]>([])
  const [hostList, setHostList] = useState<{ label: string; value: string }[]>([])

  const fishTreeData = useMemo(
    () =>
      fishingEmailList?.groupList?.map(group => ({
        title: (
          <Typography.Text ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}>
            {group.groupName}
          </Typography.Text>
        ),
        value: group.groupId,
        disabled: true,
        children: group.employeeList.map(employee => ({
          title: `${employee.email} (${employee.employeeName})`,
          value: employee.employeeId,
          disabled: true,
        })),
      })),
    [fishingEmailList],
  )

  const siteTreeData = useMemo(
    () =>
      siteList?.map(asset => ({
        title: asset.assetName,
        value: `host_${asset.assetName}`,
        key: `host_${asset.assetName}`,
        children: asset.siteList.map(site => ({
          title: `${site.sitePath}  (${site.siteName})`,
          value: `${site.sitePath}  (${site.siteName})`,
          key: `${site.sitePath}  (${site.siteName})`,
        })),
      })),
    [siteList],
  )

  const clientOptions = useMemo(
    () =>
      clientList?.map(client => ({
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography.Text ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}>
              {`${client.os}/${client.alias}/${client.name}/${client.ips.join(';')}`}
            </Typography.Text>
            <div style={{ display: 'flex' }}>
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  marginRight: '3px',
                  borderRadius: '50%',
                  background: `${client.connStatus === 'connected' ? '#67C23A' : '#666'}`,
                  marginTop: '8px',
                  marginLeft: '5px',
                }}
              />
              <span style={{ marginRight: '5px' }}>
                {client.connStatus === 'connected' ? t('在线') : t('离线')}
              </span>
            </div>
          </div>
        ),
        value: `${client.os}/${client.alias}/${client.name}/${client.ips.join(';')}`,
      })),
    [clientList],
  )

  const securityTestEmailOptions = useMemo(() => {
    if (agentIds.length === 0) {
      return []
    } else {
      return securityTestEmailList
        .filter(item => agentIds.map(v => v).includes(item.agentId))
        .map(item =>
          item.items.map(user => ({
            label: `${user.email}/${
              user.agreement === 'email_pop3'
                ? 'POP3'
                : user.agreement === 'email_imap'
                ? 'IMAP'
                : user.agreement === 'email_unknown'
                ? '-'
                : 'OUTLOOK'
            }/${user.hostName}`,
            value: `${user.email}/${
              user.agreement === 'email_pop3'
                ? 'POP3'
                : user.agreement === 'email_imap'
                ? 'IMAP'
                : user.agreement === 'email_unknown'
                ? '-'
                : 'OUTLOOK'
            }/${user.hostName}`,
          })),
        )
        .flat()
    }
  }, [agentIds])

  const onFinish = (values: any) => {
    const hostIds = values.site_id?.filter(item => item.startsWith('host_')) || []
    const partSiteIds =
      values.site_id
        ?.map(v =>
          siteList.map(site => site.siteList.find(i => i.sitePath === v.split(' ')[0])?.siteId),
        )
        .flat()
        ?.filter(item => item !== undefined) || []
    const siteIds =
      hostIds
        .map(item =>
          siteList
            .find(site => site.assetName === item.replace('host_', ''))
            ?.siteList.map(site => site.siteId),
        )
        .flat()
        .concat(partSiteIds) || []
    const clientIds =
      values.agent_id?.map(
        item =>
          clientList.find(v => v.alias === item.split('/')[1] && v.name === item.split('/')[2]).id,
      ) || []
    const emailIds =
      values.email_id
        ?.map(item =>
          securityTestEmailList
            .map(v => v.items.find(i => i.hostName === item.split('/')[2])?.id)
            .filter(user => user !== undefined),
        )
        .flat() || []
    const data = {
      name: values.name,
      company_id: [fishingEmailList.userId],
      site_id: siteIds,
      agent_id: clientIds,
      email_id: emailIds,
    }

    if (type === 'add') {
      addSecurityDomain(data)
        .then(() => {
          updateList()
          form.resetFields()
          Message.success(t('添加成功'))
          onCancel()
        })
        .catch(_ => {})
    } else {
      editSecurityDomain({ id, ...data, company_id: [fishingEmailList.userId] })
        .then(() => {
          updateList()
          form.resetFields()
          Message.success(t('编辑成功'))
          onCancel()
        })
        .catch(_ => {})
    }
  }

  useEffect(() => {
    if (visible && type === 'add') {
      getFishingEmailList({})
        .then(res => {
          form.setFieldsValue({
            company_id: res?.data?.groupList.map(group => group.groupId),
          })
        })
        .catch(_ => {})
      getSiteList({}).catch(_ => {})
      getClientList({}).catch(_ => {})
      getSecurityTestEmailList({}).catch(_ => {})
      getHostList({ page: 1, size: 10000 })
        .then(res => {
          setHostList(
            res.data.list.map(item => ({
              label: item.hostname,
              value: item.agentId,
            })),
          )
        })
        .catch(_ => {})
    }
    if (visible && type === 'edit') {
      form.setFieldsValue({
        name,
      })
      getFishingEmailList({ areaId: id })
        .then(res => {
          form.setFieldsValue({
            company_id: res?.data?.groupList.map(group => group.groupId),
          })
        })
        .catch(_ => {})
      getSiteList({ areaId: id })
        .then(res => {
          let siteIds = res?.data?.items.map(item => {
            switch (item.bindStatus) {
              case 'bind':
                return `host_${item.assetName}`
              case 'unbind':
                return
              case 'partial_bind':
                return item.siteList.map(site => {
                  if (site.bindStatus === 'bind') {
                    return `${site.sitePath}  (${site.siteName})`
                  }
                })
            }
          })

          siteIds = siteIds.flatMap(item => item).filter(item => item !== undefined)

          form.setFieldsValue({
            site_id: siteIds,
          })
        })
        .catch(_ => {})

      getClientList({ areaId: id })
        .then(res => {
          const clientIds = res?.data?.items
            .map(item => {
              switch (item.bindStatus) {
                case 'bind':
                  return `${item.os}/${item.alias}/${item.name}/${item.ips.join(';')}`
                case 'unbind':
                  return
              }
            })
            .filter(item => item !== undefined)

          const agentIdList = res?.data?.items
            .map(item => {
              switch (item.bindStatus) {
                case 'bind':
                  return item.id
                case 'unbind':
                  return
              }
            })
            .filter(item => item !== undefined)

          form.setFieldsValue({
            agent_id: clientIds,
          })

          getSecurityTestEmailList({ areaId: id })
            .then(r => {
              const securityTestEmailIds = r?.data?.items
                .filter(item =>
                  res?.data?.items.map(v => v.bindStatus === 'bind' && v.id).includes(item.agentId),
                )
                .map(v =>
                  v.items.map(
                    i =>
                      i.bindStatus === 'bind' &&
                      `${i.email}/${
                        i.agreement === 'email_pop3'
                          ? 'POP3'
                          : i.agreement === 'email_imap'
                          ? 'IMAP'
                          : i.agreement === 'email_unknown'
                          ? '-'
                          : 'OUTLOOK'
                      }/${i.hostName}`,
                  ),
                )
                .flat()
                .filter(item => item !== false)

              setAgentIds(agentIdList)
              form.setFieldsValue({
                email_id: securityTestEmailIds,
              })
            })
            .catch(_ => {})

          getHostList({ page: 1, size: 10000 })
            .then(resp => {
              setHostList(
                resp.data.list.map(item => ({
                  label: item.hostname,
                  value: item.agentId,
                })),
              )
              form.setFieldsValue({
                host_id: clientIds,
              })
            })
            .catch(_ => {})
        })
        .catch(_ => {})
    }
  }, [visible])

  return (
    <Wrapper
      open={visible}
      width={500}
      onClose={() => {
        form.resetFields()
        onCancel()
      }}
      title={<SignTitle show>{type === 'add' ? t('添加安全域') : t('编辑安全域')}</SignTitle>}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} style={{ marginRight: '10px' }}>
            {t('取消')}
          </Button>
          <Button
            type="primary"
            onClick={form.submit}
            loading={type === 'add' ? addLoading : editLoading}
          >
            {t('确定')}
          </Button>
        </div>
      }
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="name"
          label={t('安全域名称')}
          rules={[
            { required: true, message: t('请输入安全域名称') },
            { max: 15, message: t('安全域名称不能超过15个字符') },
          ]}
        >
          <Input placeholder={t('请输入安全域名称')} style={{ width: '432px' }} />
        </Form.Item>
        <div className="subtitle">{t('安全域配置')}</div>
        <Form.Item name="company_id" label={t('钓鱼邮箱配置')}>
          <TreeSelect
            treeData={fishTreeData}
            placeholder={t('请选择')}
            treeCheckable
            showSearch={false}
            showCheckedStrategy={'SHOW_PARENT'}
            style={{ width: '432px' }}
            maxTagCount={3}
            showArrow
            dropdownRender={menu => (
              <>
                {fishingEmailList?.companyName && (
                  <div style={{ fontWeight: 500, paddingLeft: '25px', marginBottom: '10px' }}>
                    <IconFont type="icon-gongsi" style={{ marginRight: '12px' }} />
                    {fishingEmailList.companyName}
                  </div>
                )}
                {menu}
              </>
            )}
          />
        </Form.Item>
        <Form.Item name="site_id" label={t('Web应用配置')}>
          <TreeSelect
            treeData={siteTreeData}
            placeholder={t('请选择')}
            treeCheckable
            showArrow
            showCheckedStrategy={'SHOW_PARENT'}
            style={{ width: '432px' }}
          />
        </Form.Item>
        <Form.Item name="agent_id" label={t('客户端配置')}>
          <Select
            mode="multiple"
            options={clientOptions}
            placeholder={t('请选择')}
            showArrow
            style={{ width: '432px' }}
            maxTagCount={1}
            onChange={(value: string[]) => {
              if (value.length > 0) {
                const clientIds =
                  value?.map(
                    item =>
                      clientList.find(
                        v => v.alias === item.split('/')[1] && v.name === item.split('/')[2],
                      ).id,
                  ) || []
                setAgentIds(clientIds)
                form.setFieldsValue({
                  host_id: clientIds,
                })
              } else {
                const clientIds =
                  value?.map(
                    item =>
                      clientList.find(
                        v => v.alias === item.split('/')[1] && v.name === item.split('/')[2],
                      ).id,
                  ) || []
                setAgentIds(clientIds)
                form.setFieldsValue({
                  email_id: [],
                })
              }
            }}
          />
        </Form.Item>
        <Form.Item name="email_id" label={t('验证邮箱配置（关联客户端选择）')}>
          <Select
            mode="multiple"
            showArrow
            options={securityTestEmailOptions}
            placeholder={t('请选择')}
            style={{ width: '432px' }}
            maxTagCount={1}
          />
        </Form.Item>
        <Form.Item name="host_id" label={t('验证主机配置（关联客户端选择）')}>
          <Select
            disabled
            mode="multiple"
            showArrow
            options={hostList}
            placeholder={t('请选择')}
            style={{ width: '432px' }}
            maxTagCount={3}
          />
        </Form.Item>
      </Form>
    </Wrapper>
  )
}

export default AddDrawer

const Wrapper = styled(Drawer)`
  .ant-drawer-content {
    border-radius: 8px;
    position: relative;

    .ant-drawer-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      margin-right: 0px;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: 0px;
      top: 0px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-10px, 8px);
        box-shadow: 0 0 0 0;
      }

      .ant-drawer-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-drawer-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
    }
  }

  .subtitle {
    font-weight: 500;
    margin-bottom: 20px;
  }
`
